import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { adminApi, fmt, formatApiError } from "../../lib/shopApi";

const inputCls = "w-full bg-[#1C1C1C] border border-[#C9A24B]/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C9A24B] transition-colors";

export const DiscountsTab = () => {
  const [discounts, setDiscounts] = useState(null);
  const [form, setForm] = useState({ code: "", type: "percent", value: "" });
  const [error, setError] = useState("");

  const load = () => adminApi.get("/discounts").then((r) => setDiscounts(r.data.discounts)).catch(() => setDiscounts([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setError("");
    if (!form.code.trim() || !form.value) {
      setError("Enter a code and value.");
      return;
    }
    try {
      await adminApi.post("/discounts", {
        code: form.code,
        type: form.type,
        value: form.type === "percent" ? parseInt(form.value) : Math.round(parseFloat(form.value) * 100),
      });
      setForm({ code: "", type: "percent", value: "" });
      load();
    } catch (e) {
      setError(formatApiError(e, "Could not create discount"));
    }
  };

  const toggle = async (d) => {
    await adminApi.put(`/discounts/${d.id}`, { ...d, active: !d.active });
    load();
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete code ${d.code}?`)) return;
    await adminApi.delete(`/discounts/${d.id}`);
    load();
  };

  if (!discounts) return <p className="text-[#F5EFE3]/40 text-sm py-10 text-center">Loading discounts…</p>;

  return (
    <div data-testid="discounts-tab">
      <div className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-5 mb-6">
        <p className="text-[#C9A24B] text-xs uppercase tracking-widest mb-4">New Discount Code</p>
        <div className="grid sm:grid-cols-4 gap-3">
          <input placeholder="CODE e.g. WELCOME10" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={inputCls} data-testid="discount-code" />
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls} data-testid="discount-type">
            <option value="percent">Percentage (%)</option>
            <option value="fixed">Fixed amount (R)</option>
          </select>
          <input type="number" min="0" placeholder={form.type === "percent" ? "e.g. 10" : "e.g. 50.00"} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputCls} data-testid="discount-value" />
          <button onClick={create} data-testid="create-discount-btn" className="flex items-center justify-center gap-2 bg-[#C9A24B] text-[#1C1C1C] rounded-xl text-xs font-bold uppercase tracking-widest py-3 hover:-translate-y-0.5 transition-transform">
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>
        {error && <p className="text-red-400 text-sm mt-3" data-testid="discount-error">{error}</p>}
      </div>

      {discounts.length === 0 ? (
        <p className="text-[#F5EFE3]/40 text-sm py-10 text-center" data-testid="discounts-empty">No discount codes yet.</p>
      ) : (
        <div className="space-y-3">
          {discounts.map((d) => (
            <div key={d.id} className="bg-[#232323] border border-[#C9A24B]/10 rounded-2xl p-5 flex flex-wrap items-center gap-4" data-testid={`discount-${d.code}`}>
              <div className="flex-1">
                <p className="font-semibold tracking-wider">{d.code}</p>
                <p className="text-[#F5EFE3]/50 text-sm">{d.type === "percent" ? `${d.value}% off` : `${fmt(d.value)} off`}</p>
              </div>
              <button
                onClick={() => toggle(d)}
                data-testid={`toggle-discount-${d.code}`}
                className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${d.active ? "border-green-400/40 text-green-300" : "border-white/20 text-white/40"}`}
              >
                {d.active ? "Active" : "Inactive"}
              </button>
              <button onClick={() => remove(d)} data-testid={`delete-discount-${d.code}`} className="p-2 text-[#F5EFE3]/40 hover:text-red-400 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
